<template>
  <div class="share-bar">
    <div class="share-bar__inner">
      <div class="share-bar__intro">
        <span class="share-bar__icon"><v-icon color="#0f7a7a">mdi-share-variant</v-icon></span>
        <div>
          <strong class="share-bar__title">Know someone with a box of old tapes?</strong><br />
          <span class="share-bar__sub">Share this page with family and friends.</span>
        </div>
      </div>

      <!-- Phone: one native share button -->
      <button
        v-if="canNativeShare"
        type="button"
        data-share="native"
        class="t2d-btn t2d-btn--outline share-bar__native"
        @click="nativeShare"
      >
        <v-icon color="#0f7a7a" small>mdi-share-variant</v-icon>Share this page
      </button>

      <div v-else class="share-bar__buttons">
        <a
          v-for="p in platforms"
          :key="p.key"
          :href="p.href"
          target="_blank"
          rel="noopener"
          :data-share="p.key"
          :aria-label="'Share on ' + p.label"
          class="share-bar__btn"
          :style="{ background: p.bg, color: p.fg }"
          @click="onShare(p.key)"
        >
          <v-icon small :color="p.fg">{{ p.icon }}</v-icon>{{ p.label }}
        </a>
        <button type="button" data-share="copy" class="share-bar__btn share-bar__copy" @click="copy">
          <v-icon small color="#0f7a7a">mdi-link-variant</v-icon>{{ copied ? "Copied" : "Copy link" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Never shares a personal link: on customer pages (route meta.personal) it
// shares the market homepage instead of the current URL.
import { market } from "@/utils/market";
import { track } from "@/utils/tracking";

export default {
  data: () => ({
    copied: false,
    canNativeShare: false,
  }),
  computed: {
    shareUrl() {
      if (this.$route.meta && this.$route.meta.personal) return market().website + "/";
      return window.location.origin + this.$route.path.replace(/\/+$/, "");
    },
    shareText() {
      return "Tapes To Digital converts old video tapes, film and photos to digital, locally.";
    },
    platforms() {
      const url = encodeURIComponent(this.shareUrl);
      const text = encodeURIComponent(this.shareText);
      return [
        { key: "facebook", label: "Facebook", icon: "mdi-facebook", bg: "#1877F2", fg: "#ffffff", href: `https://www.facebook.com/sharer/sharer.php?u=${url}` },
        { key: "whatsapp", label: "WhatsApp", icon: "mdi-whatsapp", bg: "#25D366", fg: "#0b141a", href: `https://wa.me/?text=${text}%20${url}` },
        { key: "messenger", label: "Messenger", icon: "mdi-facebook-messenger", bg: "#0866FF", fg: "#ffffff", href: `fb-messenger://share/?link=${url}` },
        { key: "x", label: "X", icon: "mdi-alpha-x", bg: "#000000", fg: "#ffffff", href: `https://twitter.com/intent/tweet?url=${url}&text=${text}` },
        { key: "linkedin", label: "LinkedIn", icon: "mdi-linkedin", bg: "#0A66C2", fg: "#ffffff", href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}` },
        { key: "email", label: "Email", icon: "mdi-email-outline", bg: "#4a5057", fg: "#ffffff", href: `mailto:?subject=${encodeURIComponent("Tapes To Digital")}&body=${text}%20${url}` },
      ];
    },
  },
  mounted() {
    this.canNativeShare =
      typeof navigator.share === "function" && window.matchMedia("(max-width: 600px)").matches;
  },
  methods: {
    onShare(platform) {
      track({ event: "share_click", platform });
    },
    async nativeShare() {
      this.onShare("native");
      try {
        await navigator.share({ title: "Tapes To Digital", text: this.shareText, url: this.shareUrl });
      } catch (e) {
        // cancelled
      }
    },
    async copy() {
      this.onShare("copy");
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        this.copied = true;
        setTimeout(() => (this.copied = false), 2000);
      } catch (e) {
        window.prompt("Copy this link", this.shareUrl);
      }
    },
  },
};
</script>

<style scoped>
.share-bar {
  background: #ffffff;
}
.share-bar__inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: 24px;
  box-sizing: border-box;
  border-top: 1px solid #e3e7ea;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}
.share-bar__intro {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 280px;
}
.share-bar__icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #e9f6f6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.share-bar__title {
  font-size: 17px;
}
.share-bar__sub {
  font-size: 14px;
  color: #4a5057;
}
.share-bar__buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.share-bar__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 700;
  border: 0;
  cursor: pointer;
  box-sizing: border-box;
}
.share-bar__copy {
  border: 1.5px solid #cfd5da;
  background: #ffffff;
  color: #1e1e1e;
}
.share-bar__native {
  width: 100%;
}
@media (max-width: 600px) {
  .share-bar__inner {
    padding: 20px 16px;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  .share-bar__title {
    font-size: 16px;
  }
  .share-bar__sub {
    font-size: 13px;
  }
}
</style>
